import { forwardRef, memo, useMemo, type Component, type FunctionComponent } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import partition from 'lodash/partition';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Box, { type BoxProps } from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
// Skillmore UI Components
import { fontWeightMedium, themeOptions } from '@empathco/ui-components/src/styles/themeOptions';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
// local imports
import { DevPlanTargetSkill } from '../graphql/types';
import { DevPlanTarget } from '../graphql/customTypes';
import { SkillLevel } from '../models/skill';
import { CategoryChipLeftTransparent } from '../elements/CategoryChip';
import SkillChip from '../elements/SkillChip';

const achievedLabelDisabledSx: SxProps<Theme> = { opacity: themeOptions.palette.action.disabledOpacity } as const;

type TargetSkillChipsProps = {
  readonly achieved?: boolean;
  readonly skills: DevPlanTargetSkill[];
  readonly satisfiedSkills?: Record<string, boolean> | null;
  readonly selectedSkills?: Record<string, boolean> | null;
  readonly disabled: boolean;
  readonly onSelect: (id: number, abbr: string) => void;
}

const TargetSkillChips: FunctionComponent<TargetSkillChipsProps> = ({
  achieved,
  skills,
  satisfiedSkills,
  selectedSkills,
  disabled,
  onSelect
}) => (
  <>
    {map(skills, (skill, idx) => {
      const chip = (
        <SkillChip
            key={skill.id}
            skill={skill}
            level={skill.skill_proficiency_level as SkillLevel}
            satisfied={satisfiedSkills?.[skill.id]}
            active={selectedSkills?.[skill.id]}
            onActivate={onSelect}
            disabled={disabled || Boolean(skill.is_satisfied)}
        />
      );
      return achieved && idx === 0 ? (
        <Box key={skill.id} display="inline-flex" alignItems="center" justifyContent="center">
          <Typography
              variant="body2"
              color="info.main"
              fontWeight={fontWeightMedium}
              fontStyle="italic"
              pl={1.5}
              sx={disabled ? achievedLabelDisabledSx : undefined}
              component="span"
          >
            <FormattedMessage id="hr.dev_plan.achieved_skills"/>
          </Typography>
          {chip}
        </Box>
      ) : chip;
    })}
  </>
);

type SkillsSelectorProps = {
  readonly caption: string;
  readonly target: DevPlanTarget | null;
  readonly targetTitle?: string;
  readonly targetGroupId: number;
  readonly targetedSkills?: DevPlanTargetSkill[] | null;
  readonly satisfiedSkills?: Record<string, boolean> | null;
  readonly selectedSkills?: Record<string, boolean> | null;
  readonly disabled?: boolean;
  readonly pending?: boolean;
  readonly onTargetClick?: () => void;
  readonly onTargetChange: () => void;
  readonly onSelect: (id: number, abbr: string) => void;
}

const SkillsSelector = forwardRef<Component<BoxProps>, SkillsSelectorProps>(({
  caption,
  target,
  targetTitle,
  targetGroupId,
  targetedSkills,
  satisfiedSkills,
  selectedSkills,
  disabled = false,
  pending = false,
  onTargetClick,
  onTargetChange,
  onSelect
}, ref) => {
  const [achievedSkills, remainingSkills] = useMemo(
    () => partition(targetedSkills, ({ is_satisfied }) => Boolean(is_satisfied)),
    [targetedSkills]
  );
  return (
    <>
      <CardSection ref={ref} shady top={pending}>
        {target ? (
          <Box pb={1}>
            <Typography variant="h4" component="span">
              <FormattedMessage
                  id="hr.dev_plan.target"
                  values={{
                    target,
                    title: (
                      <CategoryChipLeftTransparent
                          groupId={targetGroupId}
                          groupType="job"
                          label={targetTitle}
                          clickable={Boolean(onTargetClick)}
                          onClick={onTargetClick}
                          onDelete={onTargetChange}
                          disabled={disabled || pending}
                      />
                    )
                  }}
              />
            </Typography>
            <Button
                color="primary"
                variant="outlined"
                disabled={disabled || pending}
                startIcon={pending ? <CircularProgress size={18} color="inherit"/> : undefined}
                onClick={onTargetChange}
            >
              <FormattedMessage id={caption}/>
            </Button>
          </Box>
        ) : undefined}
        {pending ? undefined : (
          <TargetSkillChips
              skills={remainingSkills}
              satisfiedSkills={satisfiedSkills}
              selectedSkills={selectedSkills}
              disabled={disabled}
              onSelect={onSelect}
          />
        )}
        {pending || size(achievedSkills) < 1 ? undefined : (
          <TargetSkillChips
              achieved
              skills={achievedSkills}
              satisfiedSkills={satisfiedSkills}
              selectedSkills={selectedSkills}
              disabled={disabled}
              onSelect={onSelect}
          />
        )}
      </CardSection>
      {pending ? <LoadingPlaceholder flat/> : undefined}
      <Divider/>
    </>
  );
});

SkillsSelector.displayName = 'SkillsSelector';

export default memo(SkillsSelector);
